import { FC, useEffect, useState, useRef, useContext, useMemo } from "react";
import { Modal, Form, Select, Input, InputNumber, DatePicker, Button, Flex, TableProps, notification, Spin, Typography, Tag, Upload, UploadProps } from "antd";
import { Shipment, AddShipment, AddShipItem, Logistics, PackAddShip } from "@/types/shipment";
import { useTranslation } from "react-i18next";
import { SourceTypeEnum } from "@/types/common";
import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";
import dayjs from "dayjs";
import SelectSaleOrder from "@/components/Selector/SelectSaleOrder";
import SelectSaleOrderItem from "@/components/Selector/SelectSaleOrderItem";
import { SaleOrder, SaleItemState, SaleOrderItemState, Customer } from "@/types/sales";
import { Product } from "@/types/product";
import { DictLikeNumberKey } from "@/types/dict";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { CountrySelect, ProductSelectMulti } from "@/components/Selector";
import { ShipAddress, ShipAddressDefault } from "@/types/shipAddress";
import { list } from "@/apis/shipAddress";
import SelectShipAddress from "@/components/Selector/SelectCustomerAddress";
import { numFormmater } from "@/pages/Requirement/util";
import { divided, multi, noRateAmount } from "@/utils/calc";
import { orderAdd, orderUpdate, orderFetch, orderStatusUpdate, shipmentPack, shipmentLogistics, shipmentInventory } from "@/apis/shipment";
import SelectShipStatus from "@/components/Selector/SelectShipStatus";
import { DictContext } from "@/layout/context/DictContext";
import AddLogistics from "../../components/AddLogistics";
import LogisticsDetail from "../../components/LogisticsDetail";
import { PackBox, PackOrder } from "@/types/packing";
import PackDetail from "@/pages/Warehouse/pages/Packing/components/PackDetail";
import { ProductWithNum } from "@/components/Selector/ProductSelectMulti";
import { exportShipment, ProIdPriceRec } from "./utils";
import { OrderV2 } from "@/types/inventoryV2";
import { ChangeEnum, OperateEnum } from "@/types/inventory";
import OrderDetailV2 from "@/pages/Warehouse/pages/Order/components/OrderDetailV2";
import { packAddShip, orderFetch as packOrderFetch } from "@/apis/packing";
import { RateOption } from "./SelectRatePrice";
import { priceList } from "@/apis/purchase";
import SelectCustomer from "@/components/Selector/SelectCustomer";
import { useMainCurrency } from "@/layout/hooks/useMainCurrency";
import { PurchaseOrderItem } from "@/types/purchase";
import { wmslotV2Parse } from "@/utils/codeParse";

interface BaseProps {
  open: boolean;
  onClose: (status?: DictLikeNumberKey) => void;
  onSuccess: (newShipment: Shipment) => void;
  readonly?: boolean;
}

interface AddProps {
  formType: "ADD";
  shipment: null;
  packingOrderId: string;
  shipBoxes: PackBox[];
  saleItems: SaleItemState[];
  purLotMap: Record<string, PurchaseOrderItem>;
}

interface EditProps {
  formType: "EDIT";
  shipment: Shipment;
  packingOrderId: undefined;
  shipBoxes: null;
  saleItems: null;
  purLotMap: null;
}

type AddOrderEditProps = (AddProps | EditProps) & BaseProps;

type ItemType = "ADD" | "EDIT" | "DEL" | "NONE";

interface ItemData extends AddShipItem {
  product: Product | null;
  key?: string;
  itemCount?: number; // 原始数量（quantity + bquantity），仅ADD模式需要
  itemType: ItemType;
  rateKey?: string;
}

const AddOrderEdit: FC<AddOrderEditProps> = ({
  formType,
  open,
  packingOrderId,
  shipBoxes,
  onSuccess,
  onClose,
  shipment,
  saleItems,
  purLotMap,
  readonly = false
}) => {
  const { t } = useTranslation();
  const { getExchangeVal } = useMainCurrency();
  const { shipStatus, saleTradeTerm, salePayMethod, currencys, exchangeRate } = useContext(DictContext);
  const [form] = Form.useForm<PackAddShip>();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const curOrd = useSelector((state: RootState) => state.orgReducer.curOrg);

  const [sourceType, setSourceType] = useState<SourceTypeEnum | undefined>(undefined);
  const [items, setItems] = useState<ItemData[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);

  const [shipAddress, setShipAddress] = useState<ShipAddress[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<ShipAddress>();
  const [status, setStatus] = useState<number>(0);
  // const [openPack, setOpenPack] = useState(false);
  const [openLogistics, setOpenLogistics] = useState(false);
  const [detailShipment, setDetailShipment] = useState<Shipment | null>(null);
  const [packData, setPackData] = useState<PackOrder | null>(null);
  const [openPackDetail, setOpenPackDetail] = useState(false);
  const [logistics, setLogistics] = useState<Logistics | null>(null);
  const [openLogisticsDetail, setOpenLogisticsDetail] = useState(false);
  // const [openOut, setOpenOut] = useState(false);

  const [openAddItem, setOpenAddItem] = useState(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);
  const [outOrder, setOutOrder] = useState<OrderV2 | null>(null);

  const [rateOptions, setRateOptions] = useState<RateOption[]>([]);

  const [exportLoading, setExportLoading] = useState<boolean>(false);


  const getLatestPrice = (productId: string) => {
    let unitPrice = 1;
    if (purLotMap) {
      let keys = Object.keys(purLotMap).filter(key => key.includes(productId));
      let targetKey = keys[0];
      for (let i = 0; i < keys.length; i++) {
        let time = Number(wmslotV2Parse(keys[i].split('<->')[1]).lotData.productDate);
        let latestTime = Number(wmslotV2Parse(targetKey.split('<->')[1]).lotData.productDate);
        if (time > latestTime) targetKey = keys[i];
      };
      let purItem = purLotMap[targetKey];
      if (purItem) {
        let purCur = currencys.find(cur => cur.key === purItem?.order?.currency)?.desc;
        let exRate = '1';
        if (purCur) exRate = getExchangeVal(purCur, 'USD');
        unitPrice = Number(multi(purItem.priceStr, exRate));
      }
    };
    return unitPrice;
  };

  const fetchEditData = async () => {
    setHasUpdate(false);
    if (!shipment) return;
    form.setFieldsValue({
      sourceType: shipment.sourceType,
      sourceId: shipment.sourceId,
      orgId: shipment.orgId,
      customerId: shipment.customerId,
      customerName: shipment.customerName,
      consignee: shipment.consignee,
      consigneeAddress: shipment.consigneeAddress,
      consigneePhone: shipment.consigneePhone,
      destinationCountry: shipment.destinationCountry,
      planShipDate: shipment.planShipDate,
      tradeTerms: shipment.tradeTerms,
      paymentTerms: shipment.paymentTerms,
      remark: shipment.remark,
    });
    setSourceType(shipment.sourceType || undefined);
    setLoading(true);
    setStatus(shipment.status);
    try {
      const detail = await orderFetch(shipment.id);
      setDetailShipment(detail);
      setStatus(detail.status);
      const shipmentItems: ItemData[] = (detail.items || []).map((item) => ({
        key: item.id,
        productId: item.productId,
        product: item.product,
        count: item.count || 0,
        unitPrice: item.unitPrice || 0,
        currency: item.currency || "",
        amount: item.amount || 0,
        remark: item.remark || "",
        itemType: "NONE",
        uom: item.product?.uom ?? ''
      }));
      setItems(shipmentItems);

      try {
        //获取装箱单
        const packOrder = await packOrderFetch(detail.packingOrderId ?? '');
        !!packOrder && setPackData({ ...packOrder });
      } catch (error) { }
      try {
        // 获取物流信息
        const logisticsData = await shipmentLogistics(detail.id);
        !!logisticsData && setLogistics(logisticsData);
      } catch (error) { }
      try {
        // 获取出库单
        const orders = await shipmentInventory(detail.id);
        let orderData = orders.find(o => o.sourceType === SourceTypeEnum.SHIPMETN && o.opType === OperateEnum.SHIPMENT_OUT && o.type === ChangeEnum.PRE_OUT)
        if (!!orderData) setOutOrder({ ...orderData });
      } catch (error) { }
    } catch (error) { }
    finally {
      setLoading(false);
    }
  };

  const initAddData = async () => {
    form.resetFields();
    form.setFieldValue("orgId", curOrd.id);
    form.setFieldValue("planShipDate", dayjs().startOf('day').valueOf());
    if (!!packingOrderId) {
      form.setFieldValue("packingOrderId", packingOrderId);
      form.setFieldValue("packingBoxIds", shipBoxes.map(b => b.id));
      form.setFieldValue("sourceType", SourceTypeEnum.PACK);
      // let rOptionsMap: Record<string, RateOption> = {};
      let productMap: Record<string, ItemData> = {};
      // let resItems: ItemData[] = [];
      shipBoxes.forEach(box => {
        if (!!box.items?.length) {
          box.items.forEach(item => {
            // let saleItem = saleItems.filter(si => si.productId === item.productId);
            // if (!!saleItem?.length) {
            //   saleItem.forEach(s => {
            //     let key = `${s.productId}-${s.order.taxRate}-${s.price}`;
            //     if (!!!rOptionsMap[key] && s.order.taxRate) rOptionsMap[key] = {
            //       key,
            //       taxRate: s.order!.taxRate,
            //       price: s.price,
            //       product: s.product!
            //     };
            //   })
            // }
            let unitPrice = getLatestPrice(item.productId);
            let declaredAmount = Number(multi(String(unitPrice), String(item.count)));
            let key = `${item.productId}-${item.brand || ''}`;
            if (productMap[key]) {
              productMap[key].count += item.count;
              productMap[key].amount += declaredAmount;
            } else {
              productMap[key] = {
                productId: item.productId,
                product: item.product,
                count: item.count,
                unitPrice,
                currency: "USD",
                amount: declaredAmount,
                remark: '',
                key: item.productId,
                itemCount: undefined,
                itemType: "ADD",
                uom: item.product.uom
              }
            }
          })
        }
      });
      let resItems = Object.values(productMap);
      setItems([...resItems]);
      // let resOptionsRate = Object.values(rOptionsMap);
      // setRateOptions(resOptionsRate);
      let customer = saleItems[0]?.order?.customer;
      if (customer) {
        const tradeTermDesc = saleTradeTerm.find(d => d.id === customer.tradeTerm)?.key;
        const payMethodDesc = salePayMethod.find(d => d.id === customer.payMethod)?.desc;
        form.setFieldsValue({
          customerId: customer.id,
          customerName: customer.shortName || customer.name1,
          destinationCountry: customer.country,
          tradeTerms: tradeTermDesc || undefined,
          paymentTerms: payMethodDesc || undefined,
        });
        geCustomerShipAddress(customer.id);
      }
    } else {
      form.setFieldValue("sourceType", undefined);
    }
    setLogistics(null);
  }

  useEffect(() => {
    if (open && formType === "EDIT" && shipment) {
      fetchEditData();
    } else if (open && formType === "ADD") {
      initAddData();
    }
  }, [open, formType, shipment, packingOrderId, saleItems]);

  const clearSource = () => {
    // 清除相关字段
    form.setFieldsValue({
      sourceId: undefined,
      customerId: undefined,
      customerName: undefined,
      destinationCountry: undefined,
      consignee: undefined,
      consigneePhone: undefined,
      consigneeAddress: undefined,
      tradeTerms: undefined,
      paymentTerms: undefined
    });
    setItems([]);
    setShipAddress([]);
    setSelectedAddress(undefined)
  };

  const handleSourceTypeChange = (value: SourceTypeEnum) => {
    setSourceType(value);
    // 清除相关字段
    clearSource();
  };

  const geCustomerShipAddress = async (customerId: string) => {
    setLoading(true);
    try {
      let address = await list(customerId);
      if (address.body) {
        setShipAddress([...address.body]);
        let defaultAddress = address.body.find(ad => ad.default === ShipAddressDefault.Yes);
        setSelectedAddress(defaultAddress);
        !!defaultAddress && form.setFieldsValue({
          destinationCountry: defaultAddress.country,
          consignee: defaultAddress.name,
          consigneePhone: defaultAddress.phone,
          consigneeAddress: defaultAddress.address,
        })
      }
    } catch (error) { }
    finally { setLoading(false) }
  };

  const handleOrderChange = (order: SaleOrderItemState) => {
    setHasUpdate(true)
    const tradeTermDesc = saleTradeTerm.find(d => d.id === order.customer?.tradeTerm)?.key;
    const payMethodDesc = salePayMethod.find(d => d.id === order.customer?.payMethod)?.desc;
    form.setFieldsValue({
      sourceId: order.id,
      orgId: order.orgId,
      customerId: order.customerId,
      customerName: order.shortName || order.name1,
      destinationCountry: order.customer?.country || undefined,
      tradeTerms: tradeTermDesc || undefined,
      paymentTerms: payMethodDesc || undefined,
    });

    geCustomerShipAddress(order.customerId);

    // 从订单中提取 items
    const orderCurrency = currencys.find(c => c.key === order.currency)?.desc || "";
    const orderItems: ItemData[] = (order.items || []).map((item, index) => ({
      key: `order-${item.id}`,
      productId: item.productId,
      product: item.product,
      count: item.quantity || 0,
      unitPrice: Number(item.price ?? '') || 0,
      currency: orderCurrency,
      amount: Number(item.realAmount ?? '') || 0,
      remark: item.remark || "",
      itemCount: (item.quantity || 0) + (item.bquantity || 0),
      itemType: "ADD",
      uom: item.product?.uom ?? ''
    }));
    setItems(orderItems);
  };

  const handleItemChange = (item: SaleItemState) => {
    setHasUpdate(true)
    const order = item.order;
    const tradeTermDesc = saleTradeTerm.find(d => d.key === order?.customer?.tradeTerm)?.key;
    const payMethodDesc = salePayMethod.find(d => d.key === order?.customer?.payMethod)?.desc;
    form.setFieldsValue({
      sourceId: item.id,
      orgId: order?.orgId || "",
      customerId: order?.customerId || "",
      customerName: order?.shortName || order?.name1 || "",
      destinationCountry: order?.customer?.country || undefined,
      tradeTerms: tradeTermDesc || undefined,
      paymentTerms: payMethodDesc || undefined,
    });
    geCustomerShipAddress(order.customerId);
    // 从单个订单条目中提取 item
    const orderCurrency = currencys.find(c => c.key === order?.currency)?.desc || "";
    const orderItem: ItemData = {
      key: `item-${item.id}`,
      productId: item.productId,
      product: item.product,
      count: item.quantity || 0,
      unitPrice: Number(item.price ?? '') || 0,
      currency: orderCurrency,
      amount: Number(item.realAmount ?? '') || 0,
      remark: item.remark || "",
      itemCount: (item.quantity || 0) + (item.bquantity || 0),
      itemType: "ADD",
      uom: item.product?.uom ?? ''
    };
    setItems([orderItem]);
  };

  const handleAddress = (addressId: string) => {
    setHasUpdate(true)
    let address = shipAddress.find(add => add.id === addressId)!;
    setSelectedAddress(address);
    form.setFieldsValue({
      destinationCountry: address.country,
      consignee: address.name,
      consigneePhone: address.phone,
      consigneeAddress: address.address,
    })
  }

  const handleItemFieldChange = (key: string, field: keyof ItemData, value: any, extVal?: RateOption) => {
    setHasUpdate(true)
    const newItems = items.map(item => {
      if (item.key === key) {
        const updated: ItemData = { ...item, [field]: value, itemType: "EDIT" };
        // 计算金额
        if (field === "rateKey" && extVal) {
          let noRatePrice = noRateAmount(extVal.price, divided(extVal.taxRate, "100"));
          let amount = multi(noRatePrice, (updated.count || 0) + '');
          updated.unitPrice = Number(noRatePrice) === Infinity ? 0 : (Number(noRatePrice) || 0);
          updated.amount = Number(amount) === Infinity ? 0 : (Number(amount) || 0);
        }
        if (field === "count" || field === "unitPrice") {
          updated.amount = Number(multi(String(updated.count || 0), String(updated.unitPrice || 0)));
        }
        return updated;
      }
      return item;
    });
    setItems(newItems);
  };

  const handleIndexItemFieldChange = (index: number, field: keyof ItemData, value: any, extVal?: RateOption) => {
    setHasUpdate(true);
    let resItems = [...items];
    let newItem: ItemData = { ...resItems[index], [field]: value, itemType: "EDIT" };
    // 计算金额
    if (field === "rateKey" && extVal) {
      let noRatePrice = noRateAmount(extVal.price, divided(extVal.taxRate, "100"));
      let amount = multi(noRatePrice, (newItem.count || 0) + '');
      newItem.unitPrice = Number(noRatePrice) === Infinity ? 0 : (Number(noRatePrice) || 0);
      newItem.amount = Number(amount) === Infinity ? 0 : (Number(amount) || 0);
    }
    if (field === "count" || field === "unitPrice") {
      newItem.amount = Number(multi(String(newItem.count || 0), String(newItem.unitPrice || 0)));
    }
    resItems[index] = { ...newItem };
    setItems(resItems);
  };

  const handleUpdateStatus = async (shipStatus: number) => {
    try {
      setLoading(true);
      await orderStatusUpdate(shipment!.id, shipStatus);
      setStatus(shipStatus);
      setLoading(false);
      notification.success({ message: t('common.saveOk') });
    } catch (error) {
      notification.success({ message: t('common.saveFAil') });
      setLoading(false);
    }
  };

  const handleAddItem = (datas: ProductWithNum[]) => {
    const existIds = new Set(items.map(i => i.productId));
    const newItems: ItemData[] = datas
      .filter(pro => !existIds.has(pro.id))
      .map(pro => ({
        key: `add-${pro.id}`,
        productId: pro.id,
        product: pro,
        count: 0,
        unitPrice: 0,
        currency: items[0]?.currency ?? '',
        amount: 0,
        remark: "",
        itemType: "ADD",
        uom: pro.uom
      }));
    setItems([...items, ...newItems]);
    setHasUpdate(true);
  };

  // // 下载模版
  // const handleSaveTemplate = () => {
  //   generateTemplate(t);
  // };

  // // 导入数据
  // const handleImport: UploadProps['beforeUpload'] = async (file) => {
  //   setUploading(true);
  //   try {
  //     let parsedList = await parseData(file, t);
  //     let addList: ImportProduct[] = [];
  //     let repeatList: string[] = [];
  //     parsedList.forEach(add => {
  //       if (!!items.find(item => item.product?.code === add.productCode)) repeatList.push(add.productCode)
  //       else addList.push(add)
  //     });
  //     if (addList.length > 0) {
  //       const productListRes = await productList({ params: { strs: addList.map(a => a.productCode) } });
  //       const productListMap: Record<string, Product> = {};
  //       productListRes.body.forEach(p => {
  //         productListMap[p.code] = { ...p }
  //       });
  //       let notProsList: string[] = [];
  //       const newItems: ItemData[] = [];
  //       for (let index = 0; index < addList.length; index++) {
  //         let code = addList[index].productCode
  //         if (!!productListMap[code]) {
  //           let amount = Number(multi(String(addList[index].unitPrice || 0), String(addList[index].count || 0)))
  //           newItems.push({
  //             key: `add-${productListMap[code].id}`,
  //             product: productListMap[code],
  //             itemType: "ADD",
  //             productId: productListMap[code].id,
  //             count: addList[index].count,
  //             unitPrice: addList[index].unitPrice,
  //             currency: items[0]?.currency ?? "",
  //             amount,
  //             remark: '',
  //             uom: productListMap[code].uom
  //           })
  //         } else notProsList.push(code)
  //       }
  //       // 只添加匹配到产品的数据
  //       if (newItems.length > 0) {
  //         setHasUpdate(true);
  //         setItems([...items, ...newItems]);
  //         notification.success({
  //           placement: 'top',
  //           message: t("requirement.parseDataSuccess", { count: newItems.length }),
  //           description: notProsList.length > 0 ? (
  //             <>
  //               <div style={{ color: "red" }}>{t('apiError.error26')}</div>
  //               <span style={{ color: "red" }}>{notProsList.join(', ')}</span>

  //             </>
  //           ) : repeatList.length > 0 ? (
  //             <>
  //               <div style={{ color: "orange" }}>{t('shipment.repeated')}：</div>
  //               <span style={{ color: "orange" }}>{repeatList.join(', ')}</span>
  //             </>
  //           ) : undefined
  //         });
  //       } else {
  //         notification.error({
  //           message: t('apiError.error26'),
  //           description: notProsList.length > 0 ? (
  //             <>
  //               <div style={{ color: "red" }}>{t('apiError.error26')}</div>
  //               <span style={{ color: "red" }}>{notProsList.join(', ')}</span>

  //             </>
  //           ) : repeatList.length > 0 ? (
  //             <>
  //               <div style={{ color: "orange" }}>{t('shipment.repeated')}：</div>
  //               <span style={{ color: "orange" }}>{repeatList.join(', ')}</span>
  //             </>
  //           ) : undefined
  //         })
  //       }
  //     } else if (repeatList.length > 0) {
  //       notification.info({
  //         placement: 'top',
  //         message: t("requirement.parseDataSuccess", { count: repeatList.length }),
  //         description: (
  //           <>
  //             <div style={{ color: "orange" }}>{t('shipment.repeated')}：</div>
  //             <span style={{ color: "orange" }}>{repeatList.join(', ')}</span>
  //           </>
  //         )
  //       });
  //     }
  //   } catch (error) {
  //     notification.error({ placement: 'top', message: t("requirement.invalidTemplate") });
  //   } finally {
  //     setUploading(false);
  //   }
  //   return false; // 阻止自动上传
  // };

  const handleRemoveItem = (key: string) => {
    setItems(items.map(item => item.key === key ? { ...item, itemType: "DEL" as ItemType } : item));
    setHasUpdate(true);
  };

  const handleCustomer = (customer: Customer) => {
    const tradeTermDesc = saleTradeTerm.find(d => d.id === customer?.tradeTerm)?.key;
    const payMethodDesc = salePayMethod.find(d => d.id === customer?.payMethod)?.desc;
    form.setFieldsValue({
      customerName: customer.shortName,
      destinationCountry: customer?.country || undefined,
      tradeTerms: tradeTermDesc || undefined,
      paymentTerms: payMethodDesc || undefined,
    });
    geCustomerShipAddress(customer.id);
  };

  const handleSubmit = async () => {
    if (formType === "EDIT" && !hasUpdate) return handleClose();
    try {
      if (visibleItems.filter(item => item.count > 0).length === 0) {
        notification.error({
          message: t("common.tip"),
          description: t("shipment.noProduct"),
        });
        return;
      }
      await form.validateFields();
      const values = form.getFieldsValue();
      setLoading(true);

      // 提取 AddShipItem 数据
      const shipmentItems: AddShipItem[] = visibleItems.map(({ product, key, itemType, itemCount, rateKey, ...item }) => item);

      const data: PackAddShip = {
        ...values,
        planShipDate: values.planShipDate,
        items: shipmentItems,
      };
      if (formType === "ADD") {
        let newShipment = await packAddShip(data);
        await orderStatusUpdate(newShipment.id, 5); // 状态改为全部装箱
        setHasUpdate(false);
        setLoading(false);
        notification.success({ message: t('common.addSuccess'), description: newShipment.number });
        onSuccess(newShipment);
      }
      if (formType === "EDIT") {
        let newShipment = await orderUpdate(shipment.id, data);
        setHasUpdate(false);
        setLoading(false);
        notification.success({ message: t('common.saveOk') });
        onSuccess(newShipment);
      }
    } catch (error) {
      setLoading(false);
      notification.error({ message: t(formType === "ADD" ? 'common.addError' : "common.saveFAil") });
    }
  };

  const handleClose = () => {
    if (formType === "EDIT" && status !== shipment.status) {
      let info = shipStatus.find(s => s.key === status)!;
      onClose(info);
    } else onClose();
  };

  const handleExport = async () => {
    if (detailShipment && packData) {
      setExportLoading(true);
      // let priceMap = await handleUpdatePrice();
      // let priceMap: ProIdPriceRec = {};
      // let productIds = visibleItems.map(i => i.productId);
      // let prices = await priceList('', { strs: productIds });
      // (prices.body || []).sort((a, b) => b.effectiveAt - a.effectiveAt).forEach(p => {
      //   if (!!!priceMap[p.productId]) priceMap[p.productId] = { ...p };
      // });
      try {
        await exportShipment([detailShipment], [packData], currencys, exchangeRate);
      } catch (error) {
        console.error(error);
        notification.error({ message: t('common.exportErr') });
      }
    }
    setExportLoading(false);
  };

  //暂时不需要了
  // const handleUpdatePrice = async () => {
  //   if (visibleItems.length) {
  //     setLoading(true);
  //     try {
  //       let priceMap: ProIdPriceRec = {};
  //       let productIds = visibleItems.map(i => i.productId);
  //       let prices = await priceList('', { strs: productIds });
  //       (prices.body || []).sort((a, b) => b.effectiveAt - a.effectiveAt).forEach(p => {
  //         if (!!!priceMap[p.productId]) priceMap[p.productId] = { ...p };
  //       });

  //       const values = form.getFieldsValue();
  //       const newItems: ItemData[] = [];
  //       const shipmentItems: AddShipItem[] = visibleItems.map(data => {
  //         const { product, key, itemType, itemCount, rateKey, ...item } = data;
  //         let purPriceObj = priceMap[item.productId];
  //         let purCurrency = currencys.find(c => c.key === purPriceObj?.currency)?.desc ?? '';
  //         let purPrice = purPriceObj?.price ?? '0';
  //         let purTax = purPriceObj?.taxRate ?? purPriceObj?.supplier?.taxRate ?? '0';
  //         let exchangeVal = getExchangeVal(purCurrency, "USD");
  //         let noRatePrice = noRateAmount(purPrice, purTax);
  //         let price = Number(multi(noRatePrice, exchangeVal)) || item.unitPrice;
  //         let amount = Number(multi(String(item.count || 0), String(price)));
  //         newItems.push({
  //           ...data,
  //           unitPrice: price,
  //           amount,
  //         })
  //         return {
  //           ...item,
  //           unitPrice: price,
  //           amount,
  //         }
  //       });
  //       const data: PackAddShip = {
  //         ...values,
  //         planShipDate: values.planShipDate,
  //         items: shipmentItems,
  //       };
  //       if (formType === "EDIT") {
  //         await orderUpdate(shipment.id, data);
  //       }
  //       setItems([...newItems]);
  //       notification.success({ message: t('shipment.priceUpSucc') });
  //       return priceMap;
  //     } catch (error) {
  //       console.log(">>>", error);

  //       notification.error({ message: t('shipment.priceUpFail') })
  //     } finally {
  //       setLoading(false)
  //     }
  //   }
  // }

  const visibleItems = useMemo(() => items.filter(item => item.itemType !== "DEL"), [items]);

  const itemColumns: TableProps<ItemData>["columns"] = [
    {
      title: t("product.code"),
      key: "productCode",
      width: 120,
      render: (_, record) => <SText text={record.product?.code || ""} />,
    },
    {
      title: t("product.name"),
      key: "productName",
      width: 150,
      render: (_, record) => <SText text={record.product?.name || ""} />,
    },
    // {
    //   title: t("shipment.saleOrderCount"),
    //   key: "saleOrderCount",
    //   width: 100,
    //   hidden: formType === "EDIT" || !!!sourceType,
    //   render: (_, record) => <SText text={numFormmater(record.itemCount || 0)} />,
    // },
    {
      title: t("shipment.count"),
      key: "count",
      width: 100,
      render: (_, record, index) => (
        <InputNumber
          style={{ width: '100%' }}
          value={record.count}
          min={0} disabled
          formatter={(value) => numFormmater(Number(value || 0))}
          onChange={(value) => handleIndexItemFieldChange(index, "count", Number(value || 0))}
          changeOnWheel
        />
      ),
    },
    {
      title: t("warehouse.declaredUnitPrice"),
      key: "unitPrice",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          style={{ width: '100%' }}
          value={record.unitPrice}
          min={0}
          disabled={readonly}
          formatter={(value) => numFormmater(Number(value || 0))}
          onChange={(value) => handleIndexItemFieldChange(index, "unitPrice", Number(value || 0))}
          changeOnWheel
        />
      ),
    },
    {
      title: t("warehouse.declaredAmount"),
      key: "amount",
      width: 100,
      render: (_, record) => <SText text={numFormmater(record.amount || 0)} />,
    },
    {
      title: t("shipment.currency"),
      key: "currency",
      width: 100,
      render: (_, record) => (
        <SText text={record.currency} />
        // <SelectCurrencyVal
        //   value={record.currency}
        //   onChange={(value) => {
        //     handleItemFieldChange(record.key || "", "currency", value);
        //   }}
        //   placeholder={t('shipment.plCurrency')}
        //   style={{ width: "100%" }}
        //   disabled
        // />
      ),
    },
    // {
    //   title: t("purchase.taxRate1"),
    //   key: "rateKey",
    //   width: 240,
    //   hidden: formType !== "ADD",
    //   render: (_, record) => (
    //     <SelectRatePrice
    //       value={record.rateKey}
    //       list={rateOptions.filter(ro => ro.product.id === record.productId)}
    //       onChangeRate={(rate) => {
    //         handleItemFieldChange(record.key || "", "rateKey", rate.key, rate);

    //       }}
    //       placeholder={t('purchase.taxRate1')}
    //       style={{ width: "100%" }}
    //     />
    //   ),
    // },
    {
      title: t("shipment.remark"),
      key: "remark",
      width: 150,
      render: (_, record, index) => (
        <Input
          value={record.remark}
          disabled={readonly}
          onChange={(e) => handleIndexItemFieldChange(index, "remark", e.target.value)}
          placeholder={t('shipment.plRemark')}
        />
      ),
    },
    {
      title: t("common.opt"),
      key: "action",
      width: 80,
      // hidden: !!sourceType,
      hidden: true,
      render: (_, record) => (
        <Button type="link" size="small" danger onClick={() => handleRemoveItem(record.key || "")}>
          {t('common.del')}
        </Button>
      ),
    },
  ];

  // const headerChildren = (
  //   <Flex gap={6}>
  //     <Button type='primary' onClick={() => setOpenAddItem(true)} >{t('product.addProduct')}</Button>
  //     <Upload
  //       accept=".xlsx,.xls"
  //       beforeUpload={handleImport}
  //       showUploadList={false}
  //       disabled={uploading}
  //     >
  //       <Button type='dashed' loading={uploading}>{t("requirement.inputTemp")}</Button>
  //     </Upload>
  //     <Button type='link'
  //       onClick={handleSaveTemplate}
  //     >
  //       {t('requirement.downloadTemplate')}
  //     </Button>
  //   </Flex>
  // )

  return (
    <>
      <Modal
        title={formType === "ADD" ? t("shipment.add") : (
          <Flex gap={8} align="center">
            <Typography.Text>{t("shipment.edit") + ': ' + shipment.number}</Typography.Text>
            <Tag>{shipStatus.find(s => s.key === status)?.desc ?? ''}</Tag>
            {/* {!packData && !loading && <Button type="link" size="small" onClick={() => {
              if (!!!shipment?.sourceId && !!!outOrder) notification.error({ message: t('shipment.addShipOutPl') })
              else setOpenPack(true)
            }}>
              {t("shipment.addPackOrder")}
            </Button>} */}
            {!!packData && !loading && <Button type="link" size="small" onClick={() => setOpenPackDetail(true)}>
              {t("shipment.viewPackOrder")}
            </Button>}
            {!logistics && !loading && (
              <Button type="link" size="small" disabled={readonly} onClick={() => setOpenLogistics(true)}>
                {t("shipment.addLogistics")}
              </Button>
            )}
            {!!logistics && !loading && (
              <Button type="link" size="small" onClick={() => setOpenLogisticsDetail(true)}>
                {t("shipment.viewLogistics")}
              </Button>
            )}
            {formType === "EDIT" && !!!shipment.sourceId && !loading && !!outOrder && <Button onClick={() => setShowList(true)} >{t("purchase.outOrder")}</Button>}
            {/* {formType === "EDIT" && !!!shipment.sourceId && !loading && !!!outOrder && <Button type='primary' onClick={() => setOpenOut(true)} >{t('warehouse.shipmentOut')}</Button>} */}
            {formType === "EDIT" && <Button type="dashed" loading={exportLoading} onClick={() => handleExport()}>{t('warehouse.exportExcel')}</Button>}
          </Flex>
        )}
        maskClosable={false}
        open={open}
        onCancel={handleClose}
        onOk={handleSubmit}
        width={1200}
        destroyOnClose
        okButtonProps={{ disabled: loading || readonly }}
      >
        <Spin spinning={loading}>
          <Form form={form} disabled={readonly} onValuesChange={() => setHasUpdate(true)}>
            <Form.Item name="customerId" hidden={formType === "EDIT"}
              label={t('warehouse.selectCustomer')}
              rules={[{ required: true, message: t('warehouse.selectCustomerPl') }]}>
              <SelectCustomer onChangeCustomer={handleCustomer} />
            </Form.Item>
            <Flex gap={10} wrap={true}>
              {/* 隐藏字段 */}
              <Form.Item name="sourceId" hidden />
              <Form.Item name="orgId" hidden />
              <Form.Item name="packingOrderId" hidden />
              <Form.Item name="packingBoxIds" hidden />

              <Form.Item style={{ flex: 1 }}
                name="sourceType"
                label={t("shipment.sourceType")}
                hidden={true || formType === "EDIT"}
              >
                <Select value={sourceType}
                  style={{ width: "100%" }}
                  onChange={handleSourceTypeChange}
                  options={[
                    { label: t("shipment.saleOrder"), value: SourceTypeEnum.SALE },
                    { label: t("shipment.saleOrderItem"), value: SourceTypeEnum.SALE_ITEM },
                  ]}
                  allowClear
                  onClear={clearSource}
                  placeholder={t('common.plSelect')}
                />
              </Form.Item>

              <Form.Item label={t("shipment.selectOrder")} style={{ flex: 1 }} hidden={formType === "EDIT" || sourceType !== SourceTypeEnum.SALE}>
                <SelectSaleOrder value={form.getFieldValue("sourceId") || undefined}
                  disabled={!sourceType}
                  onChangeOrder={handleOrderChange}
                />
              </Form.Item>

              <Form.Item label={t("shipment.selectOrderItem")} style={{ flex: 1 }} hidden={formType === "EDIT" || sourceType !== SourceTypeEnum.SALE_ITEM}>
                <SelectSaleOrderItem value={form.getFieldValue("sourceId") || undefined}
                  disabled={!sourceType}
                  onChangeItem={handleItemChange}
                />
              </Form.Item>

              <Form.Item name="customerName" label={t("shipment.customerName")} style={{ flex: 1 }}>
                <Input placeholder={t("shipment.plCustomerName")} />
              </Form.Item>
              {
                formType === "ADD" && (
                  <Form.Item
                    style={{ flex: 1 }}
                    label={t("shipment.customerShipAddress")}
                  >
                    <SelectShipAddress
                      disabled={shipAddress.length === 0}
                      value={selectedAddress?.id}
                      onChange={handleAddress}
                      list={shipAddress}
                      style={{ flex: 1 }}
                    />
                  </Form.Item>)
              }

              <Form.Item
                style={{ flex: 1 }}
                name="consigneeAddress"
                label={t("shipment.consigneeAddress")}
                hidden={formType === "ADD" && !!sourceType}
              >
                <Input placeholder={t("shipment.plConsigneeAddress")} />
              </Form.Item>
              <Form.Item
                style={{ flex: 1 }}
                label={t("shipment.status")}
                hidden={formType === "ADD"}
              >
                <SelectShipStatus<number> disabled value={status} onChange={handleUpdateStatus} />
              </Form.Item>
            </Flex>
            <Flex gap={10}>
              <Form.Item
                name="destinationCountry"
                label={t("shipment.destinationCountry")}
                style={{ flex: 1 }}
              >
                <CountrySelect />
              </Form.Item>
              <Form.Item style={{ flex: 1 }}
                name="consignee"
                label={t("shipment.consignee")}
              >
                <Input placeholder={t("shipment.plConsignee")} />
              </Form.Item>

              <Form.Item
                name="consigneePhone"
                label={t("shipment.consigneePhone")}
                style={{ flex: 1 }}
              >
                <Input placeholder={t("shipment.plConsigneePhone")} />
              </Form.Item>

            </Flex>

            <Flex gap={10}>
              <Form.Item
                name="planShipDate"
                label={t("shipment.planShipDate")}
                rules={[{ required: true }]}
                style={{ flex: 1 }}
                getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
                normalize={(value) => value ? dayjs(value).valueOf() : undefined}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="tradeTerms"
                label={t("shipment.tradeTerms")}
                style={{ flex: 1 }}
              >
                <Input placeholder={t("shipment.plTradeTerms")} />
              </Form.Item>

              <Form.Item
                name="paymentTerms"
                label={t("shipment.paymentTerms")}
                style={{ flex: 1 }}
              >
                <Input placeholder={t("shipment.plPaymentTerms")} />
              </Form.Item>
            </Flex>

            <Form.Item name="remark" label={t("shipment.remark")}>
              <Input.TextArea rows={2} maxLength={1000} showCount placeholder={t("shipment.plRemark")} />
            </Form.Item>
          </Form>

          {/* Items 表格 */}
          <STableLayout
            dataSource={visibleItems}
            columns={itemColumns as ColumnsType<AnyObject>}
            tableLayoutRef={tableRef}
            total={visibleItems.length}
            scrollX={800}
            pagination={false}
            headerChildren={
              <Flex justify="space-between" align="baseline" gap={8} style={{ marginTop: -12 }}>
                <Typography.Title level={5} style={{ margin: 0 }}>{t("shipment.items")}</Typography.Title>
                {/* <Button type="link" disabled={readonly} onClick={() => handleUpdatePrice()}>{t('shipment.updatePrice')}</Button> */}
              </Flex>
            }
            extHeight={-200}
          />
        </Spin>
      </Modal>

      {/* {formType === "EDIT" && detailShipment && (
        <AddShipPackOrder
          open={openPack}
          shipOutOrder={outOrder}
          shipment={detailShipment}
          onClose={() => setOpenPack(false)}
          onSuccess={(newPack) => {
            setOpenPack(false);
            setPackData(newPack);
          }}
        />
      )} */}
      {formType === "EDIT" && detailShipment && (
        <AddLogistics
          open={openLogistics}
          shipment={detailShipment}
          pack={null}
          onClose={() => setOpenLogistics(false)}
          onSuccess={(newLogi) => {
            setOpenLogistics(false);
            setLogistics({ ...newLogi })
          }}
        />
      )}
      {formType === "EDIT" && logistics && (
        <LogisticsDetail
          open={openLogisticsDetail}
          logistics={logistics}
          readonly={readonly}
          onClose={() => setOpenLogisticsDetail(false)}
          onSuccess={() => {
            setOpenLogisticsDetail(false);
            fetchEditData();
          }}
        />
      )}
      {formType === "EDIT" && packData && (
        <PackDetail
          detail={packData}
          open={openPackDetail}
          readonly={readonly}
          onClose={() => setOpenPackDetail(false)}
          onConfirm={() => { setOpenPackDetail(false) }}
        />
      )}
      {openAddItem && <ProductSelectMulti
        openProduct={openAddItem}
        handleOk={handleAddItem}
        setOpenProduct={setOpenAddItem}
      />}
      {/* {openOut && !!detailShipment && <ShipmentOutForm open onClose={() => { setOpenOut(false) }} onSuccess={(order) => {
        setOpenOut(false);
        setOutOrder({ ...order })
      }}
        shipment={detailShipment}
      />} */}
      {!!showList && !!outOrder && (
        <OrderDetailV2 readonly
          handleRm={() => { }}
          id={outOrder.id}
          open={true}
          handleCancel={() => setShowList(false)}
        />
      )}
      {/* {!!showList && !!shipment && (
        <InventoryList
          fetchId={shipment.id}
          fetchType="SHIPMENT"
          number={shipment.number}
          onClose={() => setShowList(false)}
        />
      )} */}
    </>
  );
};

export default AddOrderEdit;
